import type { Product } from "@digital-garden/shared";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface CartLine { product: Product; quantity: 1 }
interface CartContextValue {
  lines: CartLine[];
  count: number;
  add: (product: Product) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);
const key = "digital-garden-cart-v2";

const loadCart = (): CartLine[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? "[]") as Array<{ product?: Product }>;
    const unique = new Map<string, CartLine>();
    for (const entry of raw) {
      if (entry.product?.id) unique.set(entry.product.id, { product: entry.product, quantity: 1 });
    }
    return [...unique.values()];
  } catch {
    return [];
  }
};

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(loadCart);
  useEffect(() => localStorage.setItem(key, JSON.stringify(lines)), [lines]);
  const value = useMemo<CartContextValue>(() => ({
    lines,
    count: lines.length,
    add: (product) => setLines((current) => current.some((line) => line.product.id === product.id)
      ? current.map((line) => line.product.id === product.id ? { product, quantity: 1 } : line)
      : [...current, { product, quantity: 1 }]),
    remove: (productId) => setLines((current) => current.filter((line) => line.product.id !== productId)),
    clear: () => setLines([]),
  }), [lines]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used inside CartProvider");
  return context;
}
